//lib
import React, { useMemo } from "react";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts/highcharts-gantt";

//src
import Exporting from "highcharts/modules/exporting";
import { COLOUR_CONSTANT, DAY_MILLISECONDS } from "../../../../utils/contants";
import ExportData from "highcharts/modules/export-data";
import patternFill from "highcharts/modules/pattern-fill";
import { handleWeekends } from "./gantt-chart-wrapper.utils";
import HighchartsTreeGrid from "highcharts/modules/treegrid";
import OfflineExporting from "highcharts/modules/offline-exporting";

// Initialize Highcharts modules
patternFill(Highcharts);
HighchartsTreeGrid(Highcharts);
Exporting(Highcharts);
ExportData(Highcharts);
OfflineExporting(Highcharts);

const GanttChartWrapper = (props, ref) => {
  const { series } = props;

  const DAY = 24 * 36e5;
  const MONTH = 30 * DAY;
  const YEAR = 365 * DAY;

  const today = Math.floor(Date.now() / DAY_MILLISECONDS) * DAY_MILLISECONDS;

  const chartOptions = useMemo(() => {
    const allDates = series.reduce((acc, pt) => {
      acc.push(pt.start, pt.end);
      return acc;
    }, []);
    const years = [...new Set(allDates.map((d) => new Date(d).getFullYear()))];
    const showYearTier = years.length > 1;
    return {
      chart: {
        type: "gantt",
        scrollablePlotArea: {
          minWidth: window.innerWidth * 1.5, // 150% of the container width
          scrollPositionX: 0,
        },
        // styledMode: false,
      },
      scrollbar: {
        enabled: false,
      },

      yAxis: {
        uniqueNames: true,
        gridLineWidth: 1,
        gridLineColor: "#e6e6e6", // Lighter grid color
        labels: {
          align: "left",
          symbol: {
            width: 8,
            height: 6,
            x: -4,
            y: -2,
          },
          style: {
            fontWeight: "bold",
            fontSize: "12px",
          },
        },
      },
      xAxis: [
        // ——— Day level ———
        {
          type: "datetime",
          tickInterval: DAY,
          startOnTick: false,
          endOnTick: false,
          gridLineWidth: 1,
          dateTimeLabelFormats: {
            day: "{value:%e}", // just the day number
            week: "{value:%e}", // treat a week-tick like a day
            month: "{value:%e}", // if ever used, show day
            year: "{value:%e}", // fallback
          },
          labels: {
            align: "center",
            y: 20,
            style: { fontSize: "10px" },
          },
        },

        // ——— Month level ———
        {
          linkedTo: 0,
          type: "datetime",
          tickInterval: MONTH,
          labels: {
            format: "{value:%b}", // Jan, Feb…
            align: "center",
            y: 10,
            style: { fontSize: "12px", fontWeight: "500" },
          },
        },

        // ——— Year level ———
        // only show if your data spans more than one year:
        ...(showYearTier
          ? [
              {
                linkedTo: 0,
                type: "datetime",
                tickInterval: YEAR,
                labels: {
                  format: "{value:%Y}", // 2024, 2025…
                  align: "center",
                  y: 0,
                  style: { fontSize: "14px", fontWeight: "bold" },
                },
                // draw this axis on top of the others
                offset: 30,
              },
            ]
          : []),
      ],
      tooltip: { xDateFormat: "%B %e, %Y" },

      credits: {
        enabled: false,
      },
      plotOptions: {
        gantt: {
          borderRadius: 5,
          dataLabels: {
            enabled: true,
            inside: true,
            style: {
              color: "#000",
              textOutline: "none",
              fontWeight: "bold",
            },
          },
        },
      },
      series: [
        {
          name: "Tasks",
          data: series,
          dataLabels: {
            enabled: true,
          },
          colorByPoint: true,
          // turboThreshold: 1000000,
        },
      ],
      exporting: {
        enabled: true,
        fallbackToExportServer: false,
        buttons: {
          contextButton: {
            menuItems: [
              "printChart",
              "downloadPDF",
              "downloadPNG",
              "downloadSVG",
            ],
          },
        },
      },
    };
  }, [series, today]);

  Highcharts.addEvent(Highcharts.Axis, "foundExtremes", (e) =>
    handleWeekends(e)
  );
  return (
    <>
      <HighchartsReact
        ref={ref}
        highcharts={Highcharts}
        constructorType={"ganttChart"}
        options={chartOptions}
        containerProps={{ style: { height: "500px", width: "100%" } }}
        oneToOne={true}
        allowChartUpdate={true}
        immutable={true}
      />
    </>
  );
};

export default React.forwardRef(GanttChartWrapper);
